import { MapContainer, Popup, TileLayer, CircleMarker } from "react-leaflet";
import 'leaflet/dist/leaflet.css';
import { useEffect, useState, useRef, useMemo } from 'react';
import { useGetForecastoverViewMap, useGetForecastOverViewMapStoreLevel } from '../../services/redux/hooks/useDemand';
import { IMapsLocation, IRoot } from '../../services/interface/IDemand';
import { useDispatch, useSelector } from 'react-redux';
import { setApplyFilter, setMapDC, setMapStore, setSelectedFlagMarker } from '../../services/redux/reducer/demandfilter.reducer';
import { Loader, Tooltip, Whisper } from 'rsuite';
import { formatter, getCenter } from '../../utils/formatter';
import { LatLngExpression } from "leaflet";

const MapComponent = ({ title }: any) => {

    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [isActive, setIsActive] = useState<number>(0);
    const [selectedMarker, setSelectedMarker] = useState<any[]>([]);
    const [mapLocationData, setMapLocationData] = useState<IMapsLocation[]>([]);
    const [isFetchedDCLocation, setIsFetchedDCLocation] = useState<boolean>(false);
    const [isFetchedStoreLocation, setIsFetchedStoreLocation] = useState<boolean>(false);

    const selectedDbFilter = useSelector((state:IRoot)=>state.dbfilter.selectedDbFilter);
    const applyFilter = useSelector((state:IRoot)=>state.dbfilter.applyFilter);
    const mapdc = useSelector((state:IRoot)=>state.dbfilter.mapdc);

    const {mutate, isSuccess, isLoading, isError, data: mapData} = useGetForecastoverViewMap();
    const {mutate: mutateStoreMap, isLoading: isStoreLoading, isSuccess: isStoreSuccess, data: storeMapData} = useGetForecastOverViewMapStoreLevel();

    const mapRef = useRef<any>();

    const handleMouseOver = (event: any, index: number) => {
        const marker = event.target;
        marker.openPopup();
    };

    const handleMouseOut = (event: any, index: number) => {
        const marker = event.target;
        marker.closePopup();
    };

    const handleClickMarker = (event: any, location: IMapsLocation, index: number) => {
        let localFlag = false;
        if (selectedMarker.length <= 0) {
            localFlag = true;
            setSelectedMarker([location.Latitude, location.Longitude]);
            dispatch(setMapStore([]));
            dispatch(setMapDC([location.branchId]));
            dispatch(setSelectedFlagMarker(
                {"location":[location.Latitude, location.Longitude],
                    "storeNumber":location.storeNumber,
                    "branchId":location.branchId,"status":true})
            )
        } else {
            if (selectedMarker[0] === location.Latitude && selectedMarker[1] === location.Longitude) {
                localFlag = false;
                setSelectedMarker([]);
                dispatch(setMapDC([]));
                dispatch(setMapStore([]));
                dispatch(setSelectedFlagMarker(
                    {"location":[],"branchId":[],"storeNumber":0,"status":false})
                )
                if(isActive){
                    dispatch(setMapStore([location.storeNumber]));
                }
            } else {
                localFlag = true;
                setSelectedMarker([location.Latitude, location.Longitude]);
                dispatch(setMapDC([location.branchId]));
                dispatch(setSelectedFlagMarker(
                    {"location":[location.Latitude, location.Longitude], 
                        "branchId":location.branchId,
                        "storeNumber":location.storeNumber,
                        "status":true})
                )
                if(isActive){
                    dispatch(setMapStore([location.storeNumber]));
                }
            }
        }
        if(isActive){
            dispatch(setMapDC([location.branchId]));
            mutateStoreMap({
                ...selectedDbFilter,
                dc: [location.branchId],
            });
        }
    };

    const drillupHandler=()=>{
        setIsActive(0);
        setSelectedMarker([]);
        dispatch(setApplyFilter("drillup"));
        dispatch(setSelectedFlagMarker([]));
        dispatch(setMapDC([]));
        dispatch(setMapStore([]));
    }

    const drilldownHandler=()=>{
        setIsActive(1);
        setSelectedMarker([]);
        dispatch(setApplyFilter("drilldown"));
        dispatch(setSelectedFlagMarker([]));
    }

    useEffect(()=>{ 
        if(applyFilter === "" || applyFilter === "applied" || applyFilter === "drillup")
        {
            if(isActive == 0){
                mutate(selectedDbFilter);
            }
        }
    },[isActive, selectedMarker, applyFilter]);

    useEffect(()=>{
        if(applyFilter == "applied"){
            if(isActive == 1){
                const mergedFilter = {
                    ...selectedDbFilter,
                    dc: selectedDbFilter.dc && selectedDbFilter.dc.length > 0 ? selectedDbFilter.dc : mapdc
                };                  
                mutateStoreMap(mergedFilter);
            }
        }
    },[isActive, applyFilter]);

    useEffect(() => {
        setIsFetchedDCLocation(false);
        setIsFetchedStoreLocation(false);
        setLoading(true);
        if (storeMapData?.data) {
            setMapLocationData(storeMapData?.data || []);
            setIsFetchedDCLocation(true);
            setIsFetchedStoreLocation(true);
            setLoading(false);
        }
        return ()=>{
            setIsFetchedDCLocation(false);
            setIsFetchedStoreLocation(false);
        }
    }, [storeMapData]);

    useEffect(()=>{
        setLoading(true);
        setIsFetchedDCLocation(false);
        setIsFetchedStoreLocation(false);
        if(isSuccess){
            setMapLocationData(mapData?.data || []);
            setIsFetchedDCLocation(true);
            setLoading(false);
        }
        return ()=>{
            setIsFetchedDCLocation(false);
        }
    },[mapData]);

    useEffect(() => {
        if (mapRef.current && isStoreSuccess) {
            const map = mapRef.current;
            const newCenter = getCenter(storeMapData?.data || []);
            map.setView(newCenter, 6);
        }
    }, [isStoreSuccess, storeMapData]);

    const forecastValues = mapLocationData?.map(location => location.totalForecast);
    const minForecast = Math.min(...forecastValues);
    const maxForecast = Math.max(...forecastValues);

    const normalizeRadius = (forecast:number, min:number, max:number, minRadius:number, maxRadius:number) => {
        const normalizedValue = ((forecast - min) / (max - min)) * (maxRadius - minRadius) + minRadius;
        if (normalizedValue === -Infinity || normalizedValue === Infinity || isNaN(normalizedValue)) {
            return minRadius;
        }
        
        return Math.max(minRadius, Math.min(normalizedValue, maxRadius));
    };
    
    const keysToShow = ["branchId", "BranchZipCode", "storeNumber", "storeZipcode", "totalForecast"];

    const center: LatLngExpression = useMemo(() => getCenter(mapLocationData), [mapLocationData]);
    return (
        <>
            <div className="w-[100%] h-full bg-[#FFF] shadow rounded px-[1%] py-[10px] 
            border-[1px] border-[#BEBEBE] flex flex-col z-[1]">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-mainGreen font-semibold chart-title self-start">
                        {title} {`${isActive == 1 && (isFetchedStoreLocation) ? "- Store" : 
                                    isActive == 0 && isFetchedDCLocation ? "- DC" : "- DC"}`}
                    </span>
                    <div className="flex gap-2">
                        <Whisper placement="top" followCursor trigger="hover" speaker={<Tooltip>{"Click to turn on Drill down"}</Tooltip>}>
                            <div
                                onClick={drilldownHandler}
                                className={`${isActive === 1 ? "bg-[#949494] text-white" : "cursor-pointer"} p-[0.8vw] flex items-center justify-center 
                        border-[1px] border-[#000] rounded-full w-[1vw] h-[1vw]`}
                            >
                                <span>
                                    ↓
                                </span>
                            </div>
                        </Whisper>
                        <Whisper placement="top" followCursor trigger="hover" speaker={<Tooltip>{"Drill Up"}</Tooltip>}>
                            <div
                                onClick={drillupHandler}
                                className={`${isActive === 0 ? "opacity-50" : "cursor-pointer"} p-[0.8vw] flex items-center justify-center 
                        border-[1px] border-[#000] rounded-full w-[1vw] h-[1vw]`}
                            >
                                <span>
                                    ↑
                                </span>
                            </div>
                        </Whisper>
                    </div>
                </div>
                {isFetchedDCLocation && mapLocationData.length > 0 && <div className="h-full flex-grow w-full bg-white rounded-sm">
                    <MapContainer
                        attributionControl={false}
                        dragging={true}
                        center={center}
                        ref={mapRef}
                        zoom={isActive == 0 ? 3 : 5}
                        maxZoom={10}
                        style={{ height: "100%", width: "100%" }}>
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        {mapLocationData?.map((location: IMapsLocation, index: number) => {
                            const lat = location?.Latitude || 0;
                            const lng = location?.Longitude || 0;
                            const radius = normalizeRadius(Number(location.totalForecast.toFixed(0)), minForecast, maxForecast, 5, 20);
                            const isClicked = selectedMarker && selectedMarker[0] === lat && selectedMarker[1] === lng;
                            return <CircleMarker
                                key={index}
                                center={[location?.Latitude != null ? location?.Latitude : 0, location?.Longitude != null ? location.Longitude : 0]}
                                radius={radius}
                                color={isClicked ? "#047100" : "#000"}
                                fillColor={isClicked ? "#86BC25" : "#949494"}
                                fillOpacity={isClicked ? 0.9 : 0.2}
                                weight={1}
                                eventHandlers={{
                                    click: (event) => handleClickMarker(event, location, index),
                                    mouseover: (event) => handleMouseOver(event, index),
                                    mouseout: (event) => handleMouseOut(event, index)
                                }}
                            >
                                <Popup>
                                    {Object.entries(location)
                                        .filter(([key]) => keysToShow.includes(key))
                                        .sort(([keyA], [keyB]) => {
                                            const indexA = keysToShow.indexOf(keyA);
                                            const indexB = keysToShow.indexOf(keyB);
                                            return indexA - indexB;
                                        })
                                        .map(([key, value]) => {
                                            let displayKey;

                                            if (key === "branchId") {
                                                displayKey = "DC";
                                            } else if (key === "totalForecast") {
                                                displayKey = "Total Forecast";
                                            } else if (key === "storeNumber" && isFetchedStoreLocation && isActive == 1) {
                                                displayKey = "Retail Store ID";
                                            } else if (key === "storeNumber") {
                                                if (isFetchedStoreLocation && isActive === 1) {
                                                    displayKey = "Retail Store ID";
                                                } else if (isFetchedDCLocation && isActive !== 1) {
                                                    return null;
                                                } else {
                                                    return null;
                                                }
                                            } else if (key === "BranchZipCode") {
                                                displayKey = "DC Zip Code";
                                            } else if (key === "storeZipcode") {
                                                displayKey = "Retail Store Zip Code";
                                            } else {
                                                displayKey = key;
                                            }

                                            return (
                                                <div key={key}>
                                                    <p className="text-fs14">
                                                        <span className="font-semibold">{displayKey}</span>:{" "}
                                                        {displayKey === "Total Forecast" ? formatter(value) : value}
                                                    </p>
                                                </div>
                                            );
                                        }).filter(Boolean)
                                    }

                                </Popup>
                            </CircleMarker>
                        })}
                    </MapContainer>
                </div>}
            </div>
        </>
    )
}

export default MapComponent;